import { useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import type { OverrideBehavior, OverrideConfig, ConditionGroup } from "@capture/shared/types";
import type { Stage } from "@capture/shared";
import type { ValidateRuleConfig, LookupSnapshot } from "@capture/rules-engine";
import { evaluateValidateRule, conditionAllows } from "@capture/rules-engine";

/**
 * One field's content `validate` rule, as served by
 * `GET /job-types/:id/content-validate-bundle`. Mirrors the API row shape
 * (built from the published content's validate Behaviors).
 */
export interface ContentValidateEntry {
  rule: ValidateRuleConfig;
  severity?: string;
  overrideBehavior?: OverrideBehavior;
  overrideConfig?: OverrideConfig;
  /** Task 12b — the authored branch-IF (Automations builder). Absent for
   *  derived (field-settings) bindings. An entry whose condition is not met
   *  against the current field values is skipped entirely — it contributes
   *  nothing, the same as at the server (run-content-validate.ts /
   *  run-content-batch-gate.ts), so client and server can never disagree on
   *  whether a check fires. */
  condition?: ConditionGroup;
}

/** Field content-validate rules keyed by field internalName.
 *  F-037 — each field carries ALL its bindings (walk order); the client
 *  evaluates the array on blur and reports the first failure. */
export type ContentValidateBundle = Record<string, ContentValidateEntry[]>;

/** P1 — the blur-time evaluation context for an entry's authored branch-IF.
 *  `fields` is the doc's current name->value snapshot (as before); the two new
 *  optional slices give a condition referencing a `confidence` source or a
 *  `lookup` source the same context the server builds, so client and server
 *  can never disagree on whether a check fires. `lookupData` is capped
 *  server-side (large tables are omitted from the bundle — see
 *  content-validate-bundle.ts); an omitted table resolves null here, matching a
 *  fail-open client and leaving the server as the authoritative enforcer. */
export interface ClientEvalCtx {
  fields: Record<string, string | number | boolean | null>;
  fieldConfidence?: Record<string, number>;
  lookupData?: Record<string, LookupSnapshot>;
  /** P8 — job-level constants (name→value) carried in the bundle so a blur-time
   *  branch-IF referencing a `constant` value or a `{const.*}` formula resolves
   *  the same way the server does. */
  constants?: Record<string, string | number>;
}

/**
 * F-037 — THE winner-selection policy for a field's binding array: first
 * failing entry (with its evaluation result), else the first entry (passing).
 * Every client consumer (blur evaluator, Ctrl+O override policy) uses this so
 * the acting binding can never drift between surfaces.
 *
 * Task 12b — an entry carrying an authored branch-IF (`condition`) that is
 * NOT met against `fields` is skipped entirely: it neither wins as a failure
 * nor as the passing entry. `fields` mirrors the EvalContext the server
 * builds from the SAME document field values (see run-content-validate.ts) —
 * callers pass the doc's current name->value snapshot, including this
 * field's just-committed value.
 *
 * Task 12b fix — if `entries` is non-empty but EVERY entry is gated out
 * (none applicable), that is a real "this field is clear" result, not
 * "nothing to report": the field HAS bindings in play, so gating must
 * contribute an explicit pass — otherwise a stale `fail` set while the gate
 * WAS met (e.g. a previous blur) never clears once the gate becomes unmet,
 * and accept blocks forever (see applyValidationResults in state/store.ts,
 * which treats a null/absent result as "leave status untouched"). A field
 * with ZERO bundle entries (`entries.length === 0`) still returns null —
 * untouched — so unrelated status sources (e.g. confidence gating) are never
 * clobbered for fields with no authored/derived bindings at all.
 */
export function pickActiveEntry(
  entries: ContentValidateEntry[],
  value: string,
  ctx: ClientEvalCtx = { fields: {} },
): { entry: ContentValidateEntry; valid: boolean; message?: string } | null {
  let firstApplicable: ContentValidateEntry | undefined;
  for (const entry of entries) {
    if (!conditionAllows(entry.condition, ctx)) continue;
    firstApplicable ??= entry;
    const r = evaluateValidateRule(entry.rule, value);
    if (!r.valid) return { entry, valid: false, message: r.message };
  }
  if (firstApplicable) return { entry: firstApplicable, valid: true };
  if (entries.length > 0) return { entry: entries[0]!, valid: true };
  return null;
}

interface BundleResponse {
  rules: ContentValidateBundle;
  /** P1 — capped lookup snapshots for the tables this content references
   *  (tables over the cap are omitted; see content-validate-bundle.ts). */
  lookupData?: Record<string, LookupSnapshot>;
  /** P8 — job-level constants (uncapped — tiny job config). */
  constants?: Record<string, string | number>;
}

/**
 * Fetches the published content `validate` rules for a job type so the
 * verification station can evaluate them CLIENT-SIDE on blur (R-239 Plan B
 * §9 "client instant" — no server hop, no debounce race). The bundle is
 * fetched once per job type and cached for the station's lifetime.
 *
 * Fail-open: a null/empty jobTypeId or a failed fetch yields an empty rule
 * set and `isReady=false`, so a missing bundle simply means no client traps —
 * never a wrongly-trapped operator.
 */
export function useContentValidateBundle(
  jobTypeId: string | null,
  // F-036 — the doc's real stage (from stageForStep(currentStep)); rules
  // authored for other stages must not trap fields here.
  stage: Stage = "verification",
): { rules: ContentValidateBundle; lookupData: Record<string, LookupSnapshot>; constants: Record<string, string | number>; isReady: boolean } {
  const [rules, setRules] = useState<ContentValidateBundle>({});
  const [lookupData, setLookupData] = useState<Record<string, LookupSnapshot>>({});
  const [constants, setConstants] = useState<Record<string, string | number>>({});
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Guard on falsy (not just === null): a load-order race transiently
    // yields undefined/"" before the queued-doc jobType context populates;
    // firing GET /job-types//... would 404. (Same guard as useCurrentJobType.)
    if (!jobTypeId) {
      setRules({});
      setLookupData({});
      setConstants({});
      setIsReady(false);
      return;
    }

    let cancelled = false;
    setIsReady(false);

    // .then(onFulfilled, onRejected) — attach the rejection handler
    // synchronously (React 19 + vitest microtask rationale, see useCurrentJobType).
    api
      .get<BundleResponse>(
        `/job-types/${jobTypeId}/content-validate-bundle?stage=${stage}`,
      )
      .then(
        (resp) => {
          if (cancelled) return;
          setRules(resp.rules ?? {});
          setLookupData(resp.lookupData ?? {});
          setConstants(resp.constants ?? {});
          setIsReady(true);
        },
        () => {
          if (cancelled) return;
          setRules({});
          setLookupData({});
          setConstants({});
          setIsReady(false);
        },
      );

    return () => {
      cancelled = true;
    };
  }, [jobTypeId, stage]);

  return { rules, lookupData, constants, isReady };
}
